import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type TherapeuticTypeFilter = 'medication' | 'supplement' | 'therapy_session'

export interface TherapeuticItem {
  id: string
  therapeutic_type: TherapeuticTypeFilter | 'unknown'
  recorded_at: string
  notes?: string
  source?: string
  name?: string
  dose_text?: string
  frequency_text?: string
  label?: string
  duration_minutes?: number | null
  created_at: string
  updated_at: string
}

export interface TherapeuticFilters {
  therapeutic_type?: TherapeuticTypeFilter
  start_at?: string
  end_at?: string
  limit?: number
}

export interface CreateTherapeuticData {
  therapeutic_type: TherapeuticTypeFilter
  recorded_at?: string
  notes?: string
  name?: string
  dose_text?: string
  frequency_text?: string
  label?: string
  duration_minutes?: number | null
}

export interface UpdateTherapeuticData {
  therapeutic_id: string
  recorded_at?: string
  notes?: string
  name?: string
  dose_text?: string
  frequency_text?: string
  label?: string
  duration_minutes?: number | null
}

export function useTherapeutics(filters: TherapeuticFilters = {}) {
  const { token } = useAuth()
  const queryKey = ['therapeutics', filters]

  const q = useQuery({
    queryKey,
    queryFn: async (): Promise<TherapeuticItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (filters.therapeutic_type) params.append('therapeutic_type', filters.therapeutic_type)
      if (filters.start_at) params.append('start_at', filters.start_at)
      if (filters.end_at) params.append('end_at', filters.end_at)
      if (filters.limit) params.append('limit', String(filters.limit))
      const qs = params.toString()
      const url = `/api/therapeutics/${qs ? `?${qs}` : ''}`
      const response = await authorizedFetch(url)
      if (!response.ok) throw new Error('Failed to fetch therapeutics')
      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    therapeutics: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useCreateTherapeutic() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateTherapeuticData): Promise<TherapeuticItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/therapeutics/', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to create therapeutic')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapeutics'] })
    },
  })
}

export function useUpdateTherapeutic() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateTherapeuticData): Promise<TherapeuticItem> => {
      if (!token) throw new Error('Not authenticated')
      const { therapeutic_id, ...body } = data
      const response = await authorizedFetch(`/api/therapeutics/${therapeutic_id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to update therapeutic')
      }
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['therapeutics'] })
      queryClient.invalidateQueries({ queryKey: ['therapeutics', variables.therapeutic_id] })
    },
  })
}

export function useDeleteTherapeutic() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (therapeuticId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/therapeutics/${therapeuticId}`, { method: 'DELETE' })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to delete therapeutic')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapeutics'] })
    },
  })
}
